<template>
  <v-card flat height="90vh" class="overflow-hidden">
    <v-row no-gutters height="85vh" class="overflow-hidden">
      <v-col v-if="!mobile" cols="0" md="3" style="border-right: 2px solid #e0e0e0">
        <v-card flat height="85vh" class="overflow-y-auto">
          <v-list v-model:opened="open" bg-color="transparent">
            <v-list-item
              :class="
                viewing === 'fleet-vehicles'
                  ? 'bg-pwt_azure_40 text-pwt_black'
                  : 'bg-pwt_alabaster text-pwt_black'
              "
              class="text-caption"
              value="fleet-vehicles"
              @click="() => (viewing = 'fleet-vehicles')"
            >
              Available vehicles
            </v-list-item>
            <v-list-group value="all-vehicles" fluid>
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  :class="viewing === 'all-vehicles' ? 'bg-pwt_azure_40' : 'bg-transparent'"
                  @click="() => (viewing = 'all-vehicles')"
                >
                  <v-list-item-title class="text-caption"> All vehicles models </v-list-item-title>
                </v-list-item>
              </template>

              <v-list
                v-for="makeList in makeLists"
                :key="makeList.letter"
                bg-color="transparent"
                class="pl-5"
                density="compact"
              >
                <v-list-item class="font-weight-bold">
                  {{ makeList.letter.toUpperCase() }}
                </v-list-item>
                <v-list-item
                  v-for="(make, i) in makeList.makes"
                  :key="makeList.letter + i"
                  class="text-caption"
                  @click="() => (searchQuery = make)"
                >
                  {{ make }}
                </v-list-item>
              </v-list>
            </v-list-group>
          </v-list>
        </v-card>
      </v-col>
      <!-- mobile menu tabs -->
      <v-menu v-else :close-on-content-click="false" v-model="menuControl">
        <template v-slot:activator="{ props }">
          <v-btn
            icon="mdi-dots-vertical"
            variant="text"
            v-bind="props"
            style="position: fixed; top: 8px; z-index: 1200"
          ></v-btn>
        </template>
        <v-list v-model:opened="open" bg-color="pwt_alabaster">
          <v-list-item
            :class="
              viewing === 'fleet-vehicles'
                ? 'bg-pwt_azure_40 text-pwt_black'
                : 'bg-pwt_alabaster text-pwt_black'
            "
            class="text-caption"
            value="fleet-vehicles"
            @click="
              () => {
                viewing = 'fleet-vehicles'
                menuControl = false
              }
            "
          >
            Available vehicles
          </v-list-item>
          <v-list-group value="all-vehicles" fluid>
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :class="viewing === 'all-vehicles' ? 'bg-pwt_azure_40' : 'bg-transparent'"
                @click="
                  () => {
                    viewing = 'all-vehicles'
                  }
                "
              >
                <v-list-item-title class="text-caption"> All vehicles models </v-list-item-title>
              </v-list-item>
            </template>

            <v-list
              v-for="makeList in makeLists"
              :key="makeList.letter"
              bg-color="transparent"
              class="pl-5"
              density="compact"
            >
              <v-list-item class="font-weight-bold">
                {{ makeList.letter.toUpperCase() }}
              </v-list-item>
              <v-list-item
                v-for="(make, i) in makeList.makes"
                :key="makeList.letter + i"
                class="text-caption"
                @click="
                  () => {
                    searchQuery = make
                    menuControl = false
                  }
                "
              >
                {{ make }}
              </v-list-item>
            </v-list>
          </v-list-group>
        </v-list>
      </v-menu>
      <v-col cols="12" md="9">
        <v-card flat :height="mobile ? '100%' : '85vh'" class="overflow-y-auto">
          <v-text-field
            v-model="searchQuery"
            clearable
            hide-details
            density="compact"
            class="mx-5 mb-3 mt-5"
            bg-color="pwt_alabaster"
            placeholder="Search vehicles..."
          />

          <v-card-title v-if="viewing === 'fleet-vehicles'"> Available vehicles </v-card-title>
          <v-card-text v-if="viewing === 'fleet-vehicles'">
            <v-row v-if="stockVehicles.length">
              <VehicleGalleryCard
                v-for="(vehicle, index) in stockVehicles"
                :vehicle="vehicle"
                :key="`vehicle-card-${index}`"
                @select="emitSelectedVehicle"
                @showDetails="emitSelectedVehicle"
              />
            </v-row>
            <span v-else> No available vehicles </span>
          </v-card-text>

          <v-card-text v-if="viewing === 'all-vehicles'">
            <v-row>
              <VehicleGalleryCard
                v-for="(vehicle, index) in allVehicles"
                :vehicle="vehicle"
                :key="`generic-vehicle-card-${index}`"
                @select="emitSelectedVehicle"
                @showDetails="emitSelectedVehicle"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
import Vehicle from '@/classes/vehicle_classes/vehicle'
import VehicleGalleryCard from '@/components/vehicles/VehicleGalleryCard.vue'
import { GettersTypes, type State } from '@/store/store_types'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'

const menuControl = ref(false)

const { showGeneric = false } = defineProps<{ showGeneric?: boolean }>()

const store = useStore<State>()

const searchQuery = ref('')
const open = ref([])
const viewing = ref<'fleet-vehicles' | 'all-vehicles'>('fleet-vehicles')
const unfilteredStockVehicles = computed<Vehicle[]>(() => store.getters[GettersTypes.stockVehicles])
const unfilteredGenericVehicles = computed<Vehicle[]>(
  () => store.getters[GettersTypes.genericVehicles],
)

const stockVehicles = computed(() => {
  const unfilteredData: Vehicle[] = unfilteredStockVehicles.value
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    return unfilteredData.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query)) return vehicle
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query)) return vehicle
      if (vehicle.licensePlate && vehicle.licensePlate.toLowerCase().includes(query)) return vehicle
      if (vehicle.evModel) {
        if (vehicle.evModel.name.toLowerCase().includes(query)) return vehicle
        if (vehicle.evModel.make.toLowerCase().includes(query)) return vehicle
      }
    })
  }
  return unfilteredData
})

// list for populating make quick links on side bar
const makeLists = computed<{ letter: string; makes: string[] }[]>(() => {
  const tempArray: { letter: string; makes: string[] }[] = []
  const unfilteredData: Vehicle[] =
    (showGeneric
      ? [...unfilteredStockVehicles.value, ...unfilteredGenericVehicles.value]
      : unfilteredStockVehicles.value) ?? []
  unfilteredData.forEach((vehicle) => {
    const make = vehicle.evModel?.make
    if (!make) return
    const makeList = tempArray.find((m) => m.letter === make.toLowerCase()[0])
    if (makeList) {
      if (!makeList.makes.includes(make)) makeList.makes.push(make)
    } else {
      tempArray.push({ letter: make.toLowerCase()[0], makes: [make] })
    }
  })
  // sort tempArray by letter
  tempArray.sort((a, b) => a.letter.localeCompare(b.letter))
  return tempArray
})

const allVehicles = computed(() => {
  const unfilteredData: Vehicle[] =
    (showGeneric
      ? [...unfilteredStockVehicles.value, ...unfilteredGenericVehicles.value]
      : unfilteredStockVehicles.value) ?? []
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    return unfilteredData.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query)) return vehicle
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query)) return vehicle
      if (vehicle.licensePlate && vehicle.licensePlate.toLowerCase().includes(query)) return vehicle
      if (vehicle.evModel) {
        if (vehicle.evModel.name.toLowerCase().includes(query)) return vehicle
        if (vehicle.evModel.make.toLowerCase().includes(query)) return vehicle
      }
    })
  }
  return unfilteredData
})

const emit = defineEmits<{
  (e: 'vehicle-select', vehicle: Vehicle): void
}>()

function emitSelectedVehicle(vehicle: Vehicle) {
  emit('vehicle-select', vehicle)
}

const { mobile } = useDisplay()
</script>
<style scoped>
* :deep(.v-list-item--active > .v-list-item__overlay) {
  opacity: 0 !important;
}
</style>
